import {
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  type EnvironmentProviders,
} from '@angular/core';

import { APP_CONFIG, type AppConfig } from '../config/app-config.model';
import {
  ConsoleRealtimeTelemetrySink,
  REALTIME_TELEMETRY_SINK,
  RealtimeTelemetry,
  RealtimeTelemetrySink,
  type RealtimeTelemetryRecord,
} from './realtime-telemetry';

interface ApplicationInsightsClient {
  loadAppInsights(): void;
  trackEvent(
    event: { readonly name: string },
    properties?: Readonly<Record<string, string | number>>,
  ): void;
  trackMetric(
    metric: { readonly name: string; readonly average: number },
    properties?: Readonly<Record<string, string | number>>,
  ): void;
}

interface ApplicationInsightsModule {
  readonly ApplicationInsights: new (options: {
    readonly config: {
      readonly connectionString: string;
      readonly disableAjaxTracking?: boolean;
      readonly disableFetchTracking?: boolean;
      readonly disableExceptionTracking?: boolean;
    };
  }) => ApplicationInsightsClient;
}

export type ApplicationInsightsLoader = () => Promise<ApplicationInsightsModule>;

export const APPLICATION_INSIGHTS_LOADER = new InjectionToken<ApplicationInsightsLoader>(
  'APPLICATION_INSIGHTS_LOADER',
  { factory: () => () => import('@microsoft/applicationinsights-web') },
);

export class ApplicationInsightsRealtimeTelemetrySink extends RealtimeTelemetrySink {
  private clientPromise: Promise<ApplicationInsightsClient> | null = null;

  constructor(
    private readonly connectionString: string,
    private readonly loader: ApplicationInsightsLoader,
  ) {
    super();
  }

  override async emit(record: RealtimeTelemetryRecord): Promise<void> {
    const client = await this.getClient();
    const { event, ...properties } = record;
    const name = `AITH.Realtime.${event}`;
    client.trackEvent({ name }, properties);
    client.trackMetric({ name: `${name}.Count`, average: 1 }, {
      correlationId: record.correlationId,
    });
  }

  private getClient(): Promise<ApplicationInsightsClient> {
    this.clientPromise ??= this.loader().then((module) => {
      // FR-022 (tenant-free telemetry): the SDK's default auto-collectors would
      // emit fetch/XHR dependency URLs and raw uncaught-exception details,
      // bypassing the allowlisted RealtimeTelemetryRecord path. Disable all
      // three so only the explicit custom events/metrics above are sent.
      const client = new module.ApplicationInsights({
        config: {
          connectionString: this.connectionString,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
        },
      });
      client.loadAppInsights();
      return client;
    });
    return this.clientPromise;
  }
}

export function provideRealtimeTelemetry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    RealtimeTelemetry,
    {
      provide: REALTIME_TELEMETRY_SINK,
      useFactory: () => createRealtimeTelemetrySink(
        inject(APP_CONFIG),
        inject(APPLICATION_INSIGHTS_LOADER),
      ),
    },
  ]);
}

function createRealtimeTelemetrySink(
  config: AppConfig,
  loader: ApplicationInsightsLoader,
): RealtimeTelemetrySink {
  return config.applicationInsightsConnectionString === null
    ? new ConsoleRealtimeTelemetrySink()
    : new ApplicationInsightsRealtimeTelemetrySink(
      config.applicationInsightsConnectionString,
      loader,
    );
}